import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { StageNavAction, StageSnapshot } from "@msc/shared";

import { PauseSlide } from "../components/stage/PauseSlide.js";
import { StageFrame } from "../components/stage/StageFrame.js";
import { StageSlides } from "../components/stage/StageSlides.js";
import { openPresentChannel, type PresentChannel } from "../lib/customerView/presentChannel.js";
import { navActionForKey } from "../lib/customerView/presenterKeys.js";

import "../components/stage/stage.css";

/**
 * Die Bühne — das Fenster, das im Kundentermin auf dem Beamer liegt.
 *
 * Sie ist ein **dummer Renderer**: Beim Laden meldet sie sich über den
 * Bühnenkanal an (`ready`) und fragt den aktuellen Stand an (`state-request`);
 * danach zeichnet sie nur noch, was das Cockpit als `state` schickt. Sie hält
 * keinen eigenen Zustand, ruft keine API, kennt keine Notizen.
 *
 * GRUNDSATZ (Spec D5): Hier erscheint **nie** ein Fehler und **nie** ein
 * Spinner. Solange kein Snapshot da ist, steht die Pausenfolie — neutrales
 * Branding statt eines technischen Zwischenzustands. Der Kunde sieht diesen
 * Bildschirm, nicht wir.
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/pages/StageWindow.tsx`);
 * Abweichungen: eigene CSS-Klassen statt Tailwind, Zeichenketten über `t()`,
 * und der Erledigt-Moment hängt am Abhaken im Cockpit (`reviewed`) statt an der
 * Jira-Statuskategorie — abgehakt wird im Termin, ein Statuswechsel nicht
 * zwingend.
 *
 * **Eine Eingabe gibt es doch (#267):** Mit nur einem Bildschirm liegt der
 * Fokus im Kundenfenster, und das Cockpit hört die Tasten nicht. Weiter und
 * Zurück — Tastatur, Präsentations-Clicker oder die zwei Knöpfe, die nur bei
 * Mausbewegung kurz erscheinen — gehen deshalb als `nav` ans Cockpit. Die
 * Bühne blättert nicht selbst; was danach kommt, entscheidet das Cockpit und
 * schickt es als `state` zurück.
 */
/** So lange bleiben die Knöpfe nach der letzten Mausbewegung stehen. */
const NAV_VISIBLE_MS = 2500;

export function CustomerViewStagePage() {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<StageSnapshot | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  /** Letzter Snapshot — nur zum Erkennen des Übergangs „wird gerade abgehakt". */
  const prevRef = useRef<StageSnapshot | null>(null);
  /** Timer-Id des laufenden Erledigt-Moments — für Abbruch bei Unmount und Neustart. */
  const celebrateTimeoutRef = useRef<number | null>(null);
  const channelRef = useRef<PresentChannel | null>(null);
  const [navVisible, setNavVisible] = useState(false);
  const navTimeoutRef = useRef<number | null>(null);

  const navigate = (action: StageNavAction) => channelRef.current?.post({ type: "nav", action });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const action = navActionForKey(event);
      if (action === null) return;
      // Kein Scrollen, kein Auslösen eines fokussierten Knopfs — die Taste
      // gehört hier ausschließlich der Foliensteuerung.
      event.preventDefault();
      channelRef.current?.post({ type: "nav", action });
    };
    const onMove = () => {
      setNavVisible(true);
      if (navTimeoutRef.current !== null) window.clearTimeout(navTimeoutRef.current);
      navTimeoutRef.current = window.setTimeout(() => {
        setNavVisible(false);
        navTimeoutRef.current = null;
      }, NAV_VISIBLE_MS);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousemove", onMove);
      if (navTimeoutRef.current !== null) window.clearTimeout(navTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t("stage.windowTitle");
    return () => {
      document.title = previousTitle;
    };
  }, [t]);

  useEffect(() => {
    // Der Owner steht in der Adresse (`stageWindow.ts`); ohne ihn hört die
    // Bühne auf jedes Cockpit — der Zustand vor der Owner-Regel, den nur noch
    // der E2E-Test nutzt.
    const owner = new URLSearchParams(window.location.search).get("owner") ?? "";
    const channel = openPresentChannel(owner, (message) => {
      if (message.type !== "state") return;
      const next = message.snapshot;
      if (becameReviewed(prevRef.current, next)) {
        // Ein zweites Abhaken innerhalb der 1,8 s startet den Moment neu,
        // statt ihn vorzeitig zu beenden.
        if (celebrateTimeoutRef.current !== null) {
          window.clearTimeout(celebrateTimeoutRef.current);
        }
        setCelebrate(true);
        celebrateTimeoutRef.current = window.setTimeout(() => {
          setCelebrate(false);
          celebrateTimeoutRef.current = null;
        }, 1800);
      }
      prevRef.current = next;
      setSnapshot(next);
    });
    channelRef.current = channel;
    channel.post({ type: "ready" });
    channel.post({ type: "state-request" });

    // `beforeunload` deckt das Schließen des Fensters ab, die Aufräumfunktion
    // das Verlassen der Route — das Cockpit erfährt so in beiden Fällen, dass
    // die Bühne weg ist.
    const onUnload = () => channel.post({ type: "closed" });
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      if (celebrateTimeoutRef.current !== null) {
        window.clearTimeout(celebrateTimeoutRef.current);
      }
      channel.post({ type: "closed" });
      channelRef.current = null;
      channel.close();
    };
  }, []);

  return (
    <StageFrame snapshot={snapshot}>
      {snapshot ? <StageSlides snapshot={snapshot} /> : <PauseSlide />}
      {celebrate && (
        <div className="stage-celebrate">
          <span>✓</span>
        </div>
      )}
      {/* `onMouseDown` verhindert den Fokus: Sonst löste die nächste
          Leertaste den zuletzt geklickten Knopf ein zweites Mal aus. */}
      <div className="stage-nav" data-visible={navVisible ? "true" : "false"}>
        <button type="button" aria-label={t("stage.navPrev")} onMouseDown={(e) => e.preventDefault()} onClick={() => navigate("prev")}>
          ‹
        </button>
        <button type="button" aria-label={t("stage.navNext")} onMouseDown={(e) => e.preventDefault()} onClick={() => navigate("next")}>
          ›
        </button>
      </div>
    </StageFrame>
  );
}

/**
 * Wurde das Ticket, das gerade auf der Bühne liegt, mit diesem Snapshot
 * abgehakt? Verglichen wird DASSELBE Ticket über seinen Schlüssel — sonst
 * feierte das Weiterblättern auf ein bereits besprochenes Ticket mit.
 */
function becameReviewed(prev: StageSnapshot | null, next: StageSnapshot): boolean {
  const key = next.current?.ticket.key;
  if (!prev || !key) return false;
  const before = prev.deck.find((d) => d.key === key);
  const after = next.deck.find((d) => d.key === key);
  return before !== undefined && !before.reviewed && after?.reviewed === true;
}
